/**
 * Route Parameter Parser
 *
 * Utilities for parsing and type-coercing route parameters and query parameters.
 * Provides React hooks for type-safe parameter extraction with automatic coercion.
 *
 * @module shared/utils/routing/routeParser
 */

import { useParams, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import type { RouteParams, QueryParams } from '@shared/types/routing';

/**
 * Type coercion options for parameter parsing
 */
interface CoercionSchema {
  [key: string]: 'string' | 'number' | 'boolean' | 'string[]';
}

/**
 * Parse route path parameters with optional type coercion
 *
 * @param params - Raw parameter object from useParams()
 * @param schema - Optional schema defining expected types for each parameter
 * @returns Typed parameter object
 *
 * @example
 * ```typescript
 * // URL: /organizations/123
 * const params = useParams();
 * const typed = parseRouteParams(params, { id: 'number' });
 * // typed = { id: 123 }
 * ```
 */
export function parseRouteParams<T = RouteParams>(
  params: Record<string, string | undefined>,
  schema?: CoercionSchema
): T {
  if (!schema) {
    return params as T;
  }

  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) {
      continue;
    }

    const expectedType = schema[key];
    if (!expectedType) {
      result[key] = value;
      continue;
    }

    result[key] = coerceValue(value, expectedType);
  }

  return result as T;
}

/**
 * Parse query parameters from URL search string with optional type coercion
 *
 * @param search - URL search string (from useSearchParams() or window.location.search)
 * @param schema - Optional schema defining expected types for each parameter
 * @returns Typed query parameter object
 *
 * @example
 * ```typescript
 * // URL: /organizations?page=2&limit=20&active=true
 * const [searchParams] = useSearchParams();
 * const query = parseQueryParams(searchParams, {
 *   page: 'number',
 *   limit: 'number',
 *   active: 'boolean'
 * });
 * // query = { page: 2, limit: 20, active: true }
 * ```
 */
export function parseQueryParams<T = QueryParams>(
  search: URLSearchParams | string,
  schema?: CoercionSchema
): T {
  const searchParams = typeof search === 'string' ? new URLSearchParams(search) : search;
  const result: Record<string, unknown> = {};

  for (const [key, value] of searchParams.entries()) {
    const expectedType = schema?.[key];

    // Check if this parameter appears multiple times (array)
    const allValues = searchParams.getAll(key);

    if (allValues.length > 1) {
      // Multiple values - create array
      if (expectedType === 'string[]') {
        result[key] = allValues;
      } else if (expectedType) {
        result[key] = allValues.map(v => coerceValue(v, expectedType as 'string' | 'number' | 'boolean'));
      } else {
        result[key] = allValues;
      }
    } else {
      // Single value
      if (expectedType && expectedType !== 'string[]') {
        result[key] = coerceValue(value, expectedType);
      } else {
        result[key] = value;
      }
    }
  }

  return result as T;
}

/**
 * Coerce a string value to the specified type
 *
 * @param value - String value to coerce
 * @param type - Target type
 * @returns Coerced value
 */
function coerceValue(value: string, type: 'string' | 'number' | 'boolean' | 'string[]'): unknown {
  switch (type) {
    case 'number': {
      const num = Number(value);
      return isNaN(num) ? value : num;
    }
    case 'boolean': {
      if (value === 'true') return true;
      if (value === 'false') return false;
      return value; // Return original if not a valid boolean string
    }
    case 'string':
    case 'string[]':
    default:
      return value;
  }
}

/**
 * React hook for typed path parameters with automatic coercion
 *
 * Wraps useParams() with type coercion based on provided schema.
 *
 * @param schema - Schema defining expected types for each parameter
 * @returns Typed parameters object
 *
 * @example
 * ```typescript
 * // Route: /organizations/:id
 * function OrganizationDetail() {
 *   const params = useTypedParams<{ id: number }>({ id: 'number' });
 *   console.log(typeof params.id); // 'number'
 * }
 * ```
 */
export function useTypedParams<T = RouteParams>(schema?: CoercionSchema): T {
  const params = useParams();

  return useMemo(() => {
    return parseRouteParams<T>(params, schema);
  }, [params, schema]);
}

/**
 * React hook for typed query parameters with automatic coercion
 *
 * Wraps useSearchParams() with type coercion based on provided schema.
 * Returns both the typed params object and the setSearchParams function.
 *
 * @param schema - Schema defining expected types for each parameter
 * @returns Tuple of [typed params, setSearchParams function]
 *
 * @example
 * ```typescript
 * function OrganizationList() {
 *   const [query, setQuery] = useTypedQueryParams<{
 *     page: number;
 *     limit: number;
 *     search?: string;
 *   }>({
 *     page: 'number',
 *     limit: 'number',
 *     search: 'string'
 *   });
 *
 *   // query.page is a number
 *   console.log(typeof query.page); // 'number'
 *
 *   // Update query params
 *   setQuery(prev => ({ ...prev, page: 2 }));
 * }
 * ```
 */
export function useTypedQueryParams<T = QueryParams>(
  schema?: CoercionSchema
): [T, (params: Partial<T>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const typedParams = useMemo(() => {
    return parseQueryParams<T>(searchParams, schema);
  }, [searchParams, schema]);

  const updateParams = useMemo(() => {
    return (newParams: Partial<T>) => {
      setSearchParams(prev => {
        const current = parseQueryParams<T>(prev, schema);
        const merged = { ...current, ...newParams };

        // Convert to URLSearchParams
        const next = new URLSearchParams();
        for (const [key, value] of Object.entries(merged)) {
          if (value === null || value === undefined) {
            continue; // Skip null/undefined
          }

          if (Array.isArray(value)) {
            // Multi-value param
            value.forEach(v => next.append(key, String(v)));
          } else {
            next.set(key, String(value));
          }
        }

        return next;
      });
    };
  }, [setSearchParams, schema]);

  return [typedParams, updateParams];
}

/**
 * Parse a single query parameter with type coercion
 *
 * Utility function for extracting and coercing a single parameter.
 *
 * @param searchParams - URLSearchParams instance
 * @param key - Parameter key
 * @param type - Expected type
 * @param defaultValue - Default value if parameter is missing
 * @returns Parsed and coerced value
 *
 * @example
 * ```typescript
 * const page = parseQueryParam(searchParams, 'page', 'number', 1);
 * const limit = parseQueryParam(searchParams, 'limit', 'number', 20);
 * const active = parseQueryParam(searchParams, 'active', 'boolean', false);
 * ```
 */
export function parseQueryParam<T = unknown>(
  searchParams: URLSearchParams,
  key: string,
  type: 'string' | 'number' | 'boolean' | 'string[]',
  defaultValue?: T
): T {
  const value = searchParams.get(key);

  if (value === null) {
    return defaultValue as T;
  }

  if (type === 'string[]') {
    return searchParams.getAll(key) as T;
  }

  return coerceValue(value, type) as T;
}

/**
 * Convert typed params object to URLSearchParams
 *
 * Helper function to convert a typed object to URLSearchParams for programmatic navigation.
 *
 * @param params - Typed parameters object
 * @returns URLSearchParams instance
 *
 * @example
 * ```typescript
 * const params = { page: 2, limit: 20, tags: ['sales', 'urgent'] };
 * const searchParams = toSearchParams(params);
 * navigate({ search: searchParams.toString() });
 * ```
 */
export function toSearchParams(params: Record<string, unknown>): URLSearchParams {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) {
      continue; // Skip null/undefined
    }

    if (Array.isArray(value)) {
      // Multi-value param
      value.forEach(v => searchParams.append(key, String(v)));
    } else {
      searchParams.set(key, String(value));
    }
  }

  return searchParams;
}

/**
 * Extract query parameter defaults from schema
 *
 * Creates a default values object based on provided schema and defaults.
 *
 * @param schema - Type coercion schema
 * @param defaults - Default values for each parameter
 * @returns Object with default values applied
 *
 * @example
 * ```typescript
 * const defaults = getQueryDefaults(
 *   { page: 'number', limit: 'number', sort: 'string' },
 *   { page: 1, limit: 20, sort: 'name' }
 * );
 * // defaults = { page: 1, limit: 20, sort: 'name' }
 * ```
 */
export function getQueryDefaults<T = QueryParams>(
  schema: CoercionSchema,
  defaults: Partial<T>
): T {
  const result: Record<string, unknown> = {};

  for (const key of Object.keys(schema)) {
    if (key in defaults) {
      result[key] = defaults[key as keyof T];
    }
  }

  return result as T;
}
